import React from 'react';
import { useIntl } from 'react-intl';
import { Spin } from 'antd';
import {
  DownloadOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons';
import { CSVLink } from 'react-csv';
import moment from 'moment';
import { makeT } from '../../../BulkActions/messages';

export type CreateResult = {
  // total rows submitted (CSV data rows, excluding the header)
  total: number;
  created: number;
  failed: number;
  // original header + 'error' column, then one row per failed entry — null
  // when the server reported no per-row errors
  erroredRows: string[][] | null;
};

type Props = {
  submitting: boolean;
  // rows submitted, shown while the request is in flight
  dataRowCount: number;
  result: CreateResult | null;
};

// Step 2 of the bulk-create wizard ("Create"): the spinner while
// /fleets/users/csv is in flight, then the created/failed summary. Split out of
// BulkCreateUsersDrawer so it is presentational (props in, no local state) —
// same shape as BulkActions/steps/*.
const CreateResultStep: React.FC<Props> = ({
  submitting,
  dataRowCount,
  result,
}) => {
  const intl = useIntl();
  const t = makeT(intl);

  if (submitting || !result) {
    return (
      <div style={{ padding: '60px 0', textAlign: 'center' }}>
        <Spin size="large" />
        <div className="ba-label" style={{ marginTop: 16 }}>
          {t('fleetadmin.bulk.create.progress', { total: dataRowCount })}
        </div>
      </div>
    );
  }

  const hasErrors = result.failed > 0;

  return (
    <div data-testid="BulkCreateUsers-result">
      <div className="ba-result-head">
        <div className={`ba-result-icon${hasErrors ? '' : ' ok'}`}>
          {hasErrors ? <ExclamationCircleOutlined /> : <CheckCircleOutlined />}
        </div>
        <div>
          <div className="ba-result-title">
            {hasErrors
              ? t('fleetadmin.bulk.create.doneErrors')
              : t('fleetadmin.bulk.create.doneOk')}
          </div>
          <div className="ba-result-sub">
            {t('fleetadmin.bulk.create.createdCount', {
              count: result.created,
            })}
            {hasErrors && (
              <>
                ,{' '}
                <span style={{ color: '#EE4E4E' }}>
                  {t('fleetadmin.bulk.create.failedCount', {
                    count: result.failed,
                  })}
                </span>
              </>
            )}
            .
          </div>
        </div>
      </div>

      <div className="ba-stat-grid three">
        <div className="ba-stat">
          <div className="ba-stat-label">
            {t('fleetadmin.bulk.execute.submitted')}
          </div>
          <div className="ba-stat-value">{result.total}</div>
        </div>
        <div className="ba-stat">
          <div className="ba-stat-label">
            {t('fleetadmin.bulk.execute.succeeded')}
          </div>
          <div className="ba-stat-value green">{result.created}</div>
        </div>
        <div className="ba-stat">
          <div className="ba-stat-label">
            {t('fleetadmin.bulk.execute.failed')}
          </div>
          <div className="ba-stat-value red">{result.failed}</div>
        </div>
      </div>

      {hasErrors && result.erroredRows && (
        <div className="ba-error-box">
          <div className="ba-error-title">
            {t('fleetadmin.bulk.create.couldNotCreate', {
              count: result.failed,
            })}
          </div>
          <CSVLink
            filename={`failed-users-${moment().format('YYYY_MM_DD')}.csv`}
            // @ts-ignore react-csv types don't accept string[][]
            data={result.erroredRows}
            className="ba-csv-link"
          >
            <DownloadOutlined /> {t('fleetadmin.bulk.execute.downloadFailed')}
          </CSVLink>
        </div>
      )}
    </div>
  );
};

export default CreateResultStep;
